import React, { useMemo, useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  Button,
  SalvButton,
  SalvDropdown,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  Size,
} from '@salvapp/ui-components';
import { useLocation, useNavigate } from 'react-router';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import './AlertStatuses.scss';
import AlertStatusTable from './AlertStatusTable';
import { InternalApiAlertType, InternalApiEntityType, InternalApiScenarioType } from '../../api-internal';
import { filterByAlertType, sortByAlertType, useAlertStatuses } from './alertStatusesHelper';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { ApiSanctionType } from '../../api';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { useArchivedPage } from '../../common/useArchivedPage';
import { useMe } from '../../organisation/useMe';
import SalvPage from '../../common/SalvPage';
import { getConfigurationUrl } from '../../navigation/helper';
import { ConfigurationProduct } from '../../settings/configuration/Configuration';

export const AlertStatuses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showingArchived, navigateToArchivedPage] = useArchivedPage(undefined);

  const [, statuses] = useAlertStatuses();

  const [selectedType, setSelectedType] = useState(InternalApiAlertType.SCREENING);
  const [selectedSubType, setSelectedSubType] = useState<DropdownOption>();
  const [selectedEntityType, setSelectedEntityType] = useState<DropdownOption>();

  const filteredStatuses = useMemo(() => {
    return sortByAlertType(
      filterByAlertType(statuses, selectedType, selectedSubType?.value)
        .filter((it) => {
          if (showingArchived) {
            return it.endTime;
          } else {
            return !it.endTime;
          }
        })
        .filter((it) => {
          if (!selectedEntityType) {
            return true;
          }
          return it._configuration.entityTypes?.includes(selectedEntityType.value as InternalApiEntityType);
        }),
      selectedType
    );
  }, [statuses, selectedType, selectedSubType, selectedEntityType, showingArchived]);

  const { features } = useMe();
  const bridgeAlertsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);

  return (
    <SalvPage
      title={showingArchived ? 'Archived Statuses' : 'Alert Statuses'}
      breadcrumb={
        showingArchived
          ? { title: 'Statuses', to: '/screening/statuses' }
          : {
              title: 'Configuration',
              to: getConfigurationUrl(ConfigurationProduct.GENERAL),
            }
      }
      actions={
        !showingArchived && (
          <>
            <SalvButton
              label='New status'
              icon={faPlus}
              onClick={() => navigate('/screening/statuses/setup')}
              variant='primary'
              size={Size.SMALL}
            />
            <SalvButton label='View archived' onClick={navigateToArchivedPage} variant='muted' size={Size.SMALL} />
          </>
        )
      }
      className='alert-statuses'
    >
      <div className='m-b-24' style={{ display: 'flex', gap: '24px' }}>
        <SalvRadioToggleGroup
          defaultValue={InternalApiAlertType.SCREENING}
          label='Alert type'
          size={Size.SMALL}
          onChange={(value) => {
            setSelectedType(value as InternalApiAlertType);
            setSelectedSubType(undefined);
            setSelectedEntityType(undefined);
          }}
        >
          <SalvRadioToggleButton label='SCREENING' value={InternalApiAlertType.SCREENING} />
          <SalvRadioToggleButton label='MONITORING' value={InternalApiAlertType.MONITORING} />
          {bridgeAlertsEnabled && <SalvRadioToggleButton label='BRIDGE' value={InternalApiAlertType.BRIDGE} />}
          <SalvRadioToggleButton label='MANUAL' value={InternalApiAlertType.MANUAL} />
        </SalvRadioToggleGroup>
        <SalvDropdown
          label='Alert sub-type'
          placeholder='All'
          options={getOptions(selectedType)}
          value={selectedSubType}
          onChange={(value) => setSelectedSubType(value[0])}
          size={Size.SMALL}
        />
        <SalvDropdown
          label='Entity type'
          placeholder='All'
          options={entityTypes}
          value={selectedEntityType}
          onChange={(value) => setSelectedEntityType(value[0])}
          size={Size.SMALL}
        />
      </div>
      {location.state?.isDirty && (
        <div className='m-b-24'>
          <SalvBanner
            accent={SalvBannerAccent.STRONG}
            className='flex-grow-1'
            message='You have changed an alert status. If you are using Decision rules, then make sure that you have all the necessary alert statuses added there.'
            type={SalvBannerType.WARNING}
          >
            <Button
              onClick={() =>
                navigate({
                  pathname: '/decision-rules',
                })
              }
              variant='outline-warning-light'
            >
              Go to Settings
            </Button>
          </SalvBanner>
        </div>
      )}
      <AlertStatusTable
        key={selectedType}
        type={selectedType}
        allStatuses={filteredStatuses}
        showingHistoric={showingArchived}
        showingAllAtOnce={!selectedSubType && !selectedEntityType}
      />
    </SalvPage>
  );
};

const entityTypes = [
  {
    key: InternalApiEntityType.PERSON,
    value: InternalApiEntityType.PERSON,
    label: 'Person',
  },
  {
    key: InternalApiEntityType.TRANSACTION,
    value: InternalApiEntityType.TRANSACTION,
    label: 'Transaction',
  },
];

const getOptions = (type: InternalApiAlertType) => {
  switch (type) {
    case InternalApiAlertType.SCREENING:
      return [
        {
          key: ApiSanctionType.SANCTION,
          value: ApiSanctionType.SANCTION,
          label: 'Sanctions',
        },
        {
          key: ApiSanctionType.PEP,
          value: ApiSanctionType.PEP,
          label: 'PEP',
        },
        {
          key: ApiSanctionType.ADVERSE_MEDIA,
          value: ApiSanctionType.ADVERSE_MEDIA,
          label: 'Adverse media',
        },
        {
          key: ApiSanctionType.ADVERSE_MEDIA_ENTITY,
          value: ApiSanctionType.ADVERSE_MEDIA_ENTITY,
          label: 'Adverse media entity',
        },
        {
          key: ApiSanctionType.CUSTOM_SCREENING_LIST,
          value: ApiSanctionType.CUSTOM_SCREENING_LIST,
          label: 'Custom Lists',
        },
      ];
    case InternalApiAlertType.MONITORING:
      return [
        {
          key: InternalApiScenarioType.ONLINE,
          value: InternalApiScenarioType.ONLINE,
          label: 'Real-time',
        },
        {
          key: InternalApiScenarioType.OFFLINE,
          value: InternalApiScenarioType.OFFLINE,
          label: 'Post-event',
        },
        {
          key: InternalApiScenarioType.PERIODIC,
          value: InternalApiScenarioType.PERIODIC,
          label: 'Periodic',
        },
      ];
    case InternalApiAlertType.BRIDGE:
      return [];
    case InternalApiAlertType.MANUAL:
      return []; // Manual alerts don't have subtypes
  }
};

export default AlertStatuses;
